// src/hooks/useInventoryDashboard.js
import { useMemo } from "react";
import { clampDate, endOfDay, safeNumber, toLocalISODate, generatePeriodKeys } from "../utils/inventory/dateUtils";
import { groupKeyByPeriod } from "../utils/inventory/periodUtils";
import { toDisplayLabel, toStableKey } from "../utils/inventory/keyUtils";
import { getCostForRequest } from "../utils/inventory/costUtils";

export default function useInventoryDashboard({
  requests,
  listingById,
  usersById,
  dateFrom,
  dateTo,
  selectedMaterials,
  period,
  trendBreakdown,
  trendSeriesKeys,
  trendTopN,
}) {
  return useMemo(() => {
    const from = clampDate(dateFrom);
    const to = endOfDay(dateTo);

    if (!from || !to) {
      return {
        pie: [],
        topDonors: [],
        topMaterials: [],
        trend: [],
        pickupTrend: [],
        costTrend: [],
        totalQty: 0,
        seriesOptions: [],
        trendBySeries: [],
        pickupTrendBySeries: [],
        costTrendBySeries: [],
        activeSeries: [],
      };
    }

    const matAgg = new Map();
    const donorAgg = new Map();
    const trendAgg = new Map(); // period -> lbs
    const pickupsAgg = new Map(); // period -> pickups count
    const costAgg = new Map(); // period -> total cost

    // series maps
    const seriesTotal = new Map(); // key -> { label, totalLbs, totalPickups }
    const seriesTrend = new Map(); // key -> Map(period -> lbs)
    const seriesPickups = new Map(); // key -> Map(period -> pickups)
    const seriesCost = new Map(); // key -> Map(period -> cost)

    const allowMaterials = new Set(selectedMaterials || []);

    const addSeriesPoint = (sKey, sLabel, periodKey, lbs, pickupInc, cost) => {
      const prevT = seriesTotal.get(sKey);
      if (prevT) {
        seriesTotal.set(sKey, {
          label: prevT.label,
          totalLbs: prevT.totalLbs + lbs,
          totalPickups: prevT.totalPickups + pickupInc,
        });
      } else {
        seriesTotal.set(sKey, { label: sLabel, totalLbs: lbs, totalPickups: pickupInc });
      }

      if (!seriesTrend.has(sKey)) seriesTrend.set(sKey, new Map());
      seriesTrend.get(sKey).set(periodKey, (seriesTrend.get(sKey).get(periodKey) || 0) + lbs);

      if (!seriesPickups.has(sKey)) seriesPickups.set(sKey, new Map());
      seriesPickups
        .get(sKey)
        .set(periodKey, (seriesPickups.get(sKey).get(periodKey) || 0) + pickupInc);

      if (!seriesCost.has(sKey)) seriesCost.set(sKey, new Map());
      seriesCost.get(sKey).set(periodKey, (seriesCost.get(sKey).get(periodKey) || 0) + cost);
    };

    (requests || []).forEach((r) => {
      if (!r?.date_delivered) return;

      const delivered = clampDate(r.date_delivered);
      if (!delivered) return;

      const deliveredDay = toLocalISODate(delivered);
      if (deliveredDay < dateFrom || deliveredDay > dateTo) return;

      const listing = listingById.get(String(r.listing_id));
      if (!listing) return;

      const mat =
        listing?.material ||
        listing?.materialType ||
        listing?.material_name ||
        listing?.material_type ||
        "Unknown";

      if (allowMaterials.size > 0 && !allowMaterials.has(mat)) return;

      const companyIdRaw =
        listing?.company_id ??
        listing?.companyId ??
        listing?.user_id ??
        listing?.userId ??
        listing?.donor_id ??
        listing?.donorId ??
        null;

      const companyId = companyIdRaw != null ? String(companyIdRaw) : null;
      const mappedName = companyId ? usersById.get(companyId)?.company_name : null;

      const donorRaw =
        listing?.donor_name ??
        mappedName ??
        listing?.company_name ??
        listing?.company ??
        companyId ??
        null;

      const donorKeyStable = toStableKey(donorRaw);
      const donorLabel = toDisplayLabel(donorRaw);

      const qty =
        safeNumber(r?.quantity_delivered) ||
        safeNumber(r?.quantity) ||
        safeNumber(r?.qty) ||
        safeNumber(listing?.quantity) ||
        safeNumber(listing?.qty) ||
        0;

      const cost = getCostForRequest(r, listing, qty);

      // globals
      matAgg.set(mat, (matAgg.get(mat) || 0) + qty);

      const prev = donorAgg.get(donorKeyStable);
      if (prev) donorAgg.set(donorKeyStable, { label: prev.label, value: prev.value + qty });
      else donorAgg.set(donorKeyStable, { label: donorLabel, value: qty });

      const pKey = groupKeyByPeriod(delivered, period);
      trendAgg.set(pKey, (trendAgg.get(pKey) || 0) + qty);
      pickupsAgg.set(pKey, (pickupsAgg.get(pKey) || 0) + 1);
      costAgg.set(pKey, (costAgg.get(pKey) || 0) + cost);

      // series
      addSeriesPoint(`material:${String(mat)}`, String(mat), pKey, qty, 1, cost);
      addSeriesPoint(`donor:${donorKeyStable}`, donorLabel, pKey, qty, 1, cost);
    });

    const pie = [...matAgg.entries()]
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const topMaterials = pie.slice(0, 10);

    const topDonors = [...donorAgg.entries()]
      .map(([key, obj]) => ({ key, name: obj.label, value: obj.value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const totalQty = pie.reduce((s, d) => s + safeNumber(d.value), 0);

    // fill a complete timeline for totals
    const timelineKeys = generatePeriodKeys(from, to, period, groupKeyByPeriod);

    const trend = timelineKeys.map((pKey) => ({
      period: pKey,
      value: safeNumber(trendAgg.get(pKey) || 0),
    }));

    const pickupTrend = timelineKeys.map((pKey) => ({
      period: pKey,
      value: safeNumber(pickupsAgg.get(pKey) || 0),
    }));

    const costTrend = timelineKeys.map((pKey) => ({
      period: pKey,
      value: safeNumber(costAgg.get(pKey) || 0),
    }));

    // build seriesOptions for current breakdown
    const options = [];
    seriesTotal.forEach((obj, key) => {
      if (trendBreakdown === "material" && !key.startsWith("material:")) return;
      if (trendBreakdown === "donor" && !key.startsWith("donor:")) return;
      if (trendBreakdown === "overall") return;
      options.push({
        key,
        label: obj.label,
        totalLbs: obj.totalLbs,
        totalPickups: obj.totalPickups,
      });
    });

    options.sort((a, b) => b.totalLbs - a.totalLbs);

    const picked = new Set(trendSeriesKeys || []);
    const activeKeys =
      trendBreakdown === "overall"
        ? []
        : (trendSeriesKeys || []).length > 0
        ? options.filter((o) => picked.has(o.key)).map((o) => o.key)
        : options.slice(0, Math.max(1, trendTopN)).map((o) => o.key);

    const activeSeries = activeKeys
      .map((k) => options.find((o) => o.key === k))
      .filter(Boolean)
      .map((o) => ({ key: o.key, label: o.label }));

    const allPeriods = timelineKeys;

    const trendBySeries = allPeriods.map((p) => {
      const row = { period: p };
      activeKeys.forEach((k) => {
        row[k] = safeNumber(seriesTrend.get(k)?.get(p) || 0);
      });
      return row;
    });

    const pickupTrendBySeries = allPeriods.map((p) => {
      const row = { period: p };
      activeKeys.forEach((k) => {
        row[k] = safeNumber(seriesPickups.get(k)?.get(p) || 0);
      });
      return row;
    });

    const costTrendBySeries = allPeriods.map((p) => {
      const row = { period: p };
      activeKeys.forEach((k) => {
        row[k] = safeNumber(seriesCost.get(k)?.get(p) || 0);
      });
      return row;
    });

    return {
      pie,
      topDonors,
      topMaterials,
      trend,
      pickupTrend,
      costTrend,
      totalQty,
      seriesOptions: options,
      activeSeries,
      trendBySeries,
      pickupTrendBySeries,
      costTrendBySeries,
    };
  }, [
    requests,
    listingById,
    usersById,
    dateFrom,
    dateTo,
    selectedMaterials,
    period,
    trendBreakdown,
    trendSeriesKeys,
    trendTopN,
  ]);
}
